import { formatDateTime, formatLabel } from '@/utils/format'
import { formatCallDuration } from '@/utils/leadCallHistory'

export const CAMPAIGN_METRICS_GRANULARITIES = [
  { value: 'TOTAL', title: 'Overview' },
  { value: 'DAY', title: 'By day' },
  { value: 'HOUR_OF_DAY', title: 'By hour' },
  { value: 'DAY_OF_WEEK', title: 'By weekday' },
  { value: 'AGENT', title: 'By agent' },
]

const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

function pickNumber(...values) {
  for (const value of values) {
    if (value == null || value === '') continue
    const num = Number(value)
    if (Number.isFinite(num)) return num
  }
  return null
}

function pickString(...values) {
  for (const value of values) {
    const text = String(value ?? '').trim()
    if (text) return text
  }
  return ''
}

/** True for Cognito / request UUIDs — never show these as people or lead names. */
function isLikelyUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    String(value ?? '').trim(),
  )
}

function pickHumanLabel(...values) {
  for (const value of values) {
    const text = String(value ?? '').trim()
    if (!text || isLikelyUuid(text)) continue
    return text
  }
  return ''
}

/**
 * The backend never puts a separate hour/date/agent_id field on a bucket --
 * the ONLY place that data lives is the DynamoDB metric_key itself:
 * "TOTAL", "DAY#2026-07-14", "HOUR_OF_DAY#09", "DAY_OF_WEEK#TUE",
 * "AGENT#<id>" (see yandle_metrics_aggregator's bucketKeysFor/agentBucket).
 * Every one of the dimension-specific fields below was previously read from
 * item.hour/item.date/item.agent_id etc., which never actually existed on
 * the raw response -- this parses the real source of truth instead.
 */
function parseMetricKey(item) {
  const key = String(item?.metric_key ?? item?.metricKey ?? '')
  const hashIdx = key.indexOf('#')
  if (hashIdx === -1) return { prefix: key, value: '' }
  return { prefix: key.slice(0, hashIdx), value: key.slice(hashIdx + 1) }
}

/** "8AM-9AM" style, matching the dashboard design's hour formatting. */
function hourRangeLabel(hour) {
  const h = ((Number(hour) % 24) + 24) % 24
  const fmt = (v) => (v % 12 === 0 ? 12 : v % 12) + (v < 12 ? 'AM' : 'PM')
  return `${fmt(h)}-${fmt((h + 1) % 24)}`
}

function formatRate(value) {
  if (value == null || !Number.isFinite(Number(value))) return '—'
  const num = Number(value)
  // API may return 0–1 or 0–100.
  const pct = num <= 1 ? num * 100 : num
  return `${pct.toFixed(pct >= 10 ? 0 : 1)}%`
}

function formatScore(value) {
  if (value == null || !Number.isFinite(Number(value))) return '—'
  return Number(value).toFixed(Number(value) % 1 ? 1 : 0)
}

// KAN-118: campaign-level Total Talk Time had no aggregate anywhere -- only
// per-lead duration (leadCallHistory.js) or org-wide (useDashboardMetrics.js).
function formatTalkTime(totalSeconds) {
  if (totalSeconds == null || !Number.isFinite(Number(totalSeconds)) || totalSeconds <= 0) return '—'
  const seconds = Math.round(Number(totalSeconds))
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  if (hours > 0) return `${hours}h ${minutes}m`
  if (minutes > 0) return `${minutes}m ${secs}s`
  return `${secs}s`
}

function extractBuckets(payload) {
  if (!payload || typeof payload !== 'object') return []
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload.buckets)) return payload.buckets
  if (Array.isArray(payload.items)) return payload.items
  if (Array.isArray(payload.data)) return payload.data
  if (payload.data && typeof payload.data === 'object') {
    if (Array.isArray(payload.data.buckets)) return payload.data.buckets
    if (Array.isArray(payload.data.items)) return payload.data.items
    // TOTAL may return a single object under data.
    if (
      payload.data.calls_placed != null ||
      payload.data.callsPlaced != null ||
      payload.data.connect_rate != null ||
      payload.data.connectRate != null
    ) {
      return [payload.data]
    }
  }
  if (
    payload.calls_placed != null ||
    payload.callsPlaced != null ||
    payload.connect_rate != null
  ) {
    return [payload]
  }
  return []
}

function extractStatusCounts(item = {}) {
  // Preferred shape: the backend now returns a clean statuses[] array
  // directly (real labels, already deduped) on TOTAL/AGENT buckets --
  // use that when present. The status_<KEY> attribute-name regex below is
  // a fallback for older/other bucket shapes that don't have it.
  if (Array.isArray(item.statuses)) {
    return item.statuses
      .map((row) => {
        const status = pickString(row?.status, row?.label)
        const count = pickNumber(row?.count) || 0
        if (!status) return null
        return { key: `status_${status}`, status, label: status, count }
      })
      .filter(Boolean)
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
  }

  const counts = []
  for (const [key, value] of Object.entries(item)) {
    const match = key.match(/^status[_-]?(.+)$/i)
    if (!match) continue
    const num = Number(value)
    if (!Number.isFinite(num)) continue
    const status = String(match[1] || '')
      .replace(/([a-z])([A-Z])/g, '$1_$2')
      .replace(/-/g, '_')
      .toUpperCase()
    counts.push({
      key: `status_${status}`,
      status,
      label: formatLabel(status) || status,
      count: num,
    })
  }
  counts.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
  return counts
}

/** Connected/No answer/Failed, from the backend's call_outcomes[] (TOTAL/AGENT buckets). */
function extractCallOutcomes(item = {}) {
  if (!Array.isArray(item.call_outcomes)) return []
  return item.call_outcomes
    .map((row) => ({
      outcome: pickString(row?.outcome),
      count: pickNumber(row?.count) || 0,
    }))
    .filter((row) => row.outcome)
}

/** Short (<10s)/Medium (10-30s)/Long (>30s), from the backend's call_durations[]
 *  (TOTAL/AGENT buckets) -- connected calls only, same shape as call_outcomes above. */
function extractCallDurations(item = {}) {
  if (!Array.isArray(item.call_durations)) return []
  return item.call_durations
    .map((row) => ({
      bucket: pickString(row?.bucket),
      count: pickNumber(row?.count) || 0,
    }))
    .filter((row) => row.bucket)
}

/** Per-hour {hour, callsPlaced, callsConnected} -- an AGENT bucket's own calling pattern. */
function extractHourly(item = {}) {
  if (!Array.isArray(item.hourly)) return []
  return item.hourly
    .map((row) => ({
      hour: pickNumber(row?.hour),
      callsPlaced: pickNumber(row?.calls_placed, row?.callsPlaced) || 0,
      callsConnected: pickNumber(row?.calls_connected, row?.callsConnected) || 0,
    }))
    .filter((row) => row.hour != null)
    .sort((a, b) => a.hour - b.hour)
}

function bucketLabel(item, granularity, index = 0) {
  const gran = String(granularity || '').toUpperCase()
  const { value: keyValue } = parseMetricKey(item)

  if (gran === 'HOUR_OF_DAY') {
    const hour = pickNumber(item.hour, item.hour_of_day, item.hourOfDay, keyValue)
    if (hour != null) return hourRangeLabel(hour)
  }
  if (gran === 'DAY_OF_WEEK') {
    const raw = item.day_of_week ?? item.dayOfWeek ?? item.weekday ?? item.day ?? item.label ?? keyValue
    if (typeof raw === 'number' && raw >= 0 && raw <= 6) return DAY_NAMES[raw]
    const text = String(raw ?? '').trim()
    if (text) {
      // Backend stores 3-letter codes (MON/TUE/...) -- match against the
      // full day names rather than title-casing the abbreviation as-is.
      const full = DAY_NAMES.find((d) => d.slice(0, 3).toUpperCase() === text.toUpperCase())
      return full || formatLabel(text)
    }
  }
  if (gran === 'DAY') {
    const day = pickString(item.date, item.day, item.label, item.key, keyValue)
    if (day) return day
  }
  if (gran === 'AGENT') {
    const raw = pickString(
      item.agent_name,
      item.agentName,
      item.name,
      item.email,
      item.agent_id,
      item.agentId,
      keyValue,
    )
    // The backend's AGENT# bucket key is sanitized (spaces -> underscores,
    // see yandle_metrics_aggregator's sanitizeKeyPart) since it's a DynamoDB
    // key -- real production agent_ids are Cognito UUIDs so this is a no-op
    // for them, but restores readable spacing for any human-readable id.
    return (isLikelyUuid(raw) ? raw : raw.replace(/_/g, ' ')) || `Agent ${index + 1}`
  }
  return (
    pickString(item.label, item.key, item.name, item.title) ||
    (gran === 'TOTAL' ? 'Campaign total' : `Bucket ${index + 1}`)
  )
}

export function normalizeCampaignMetricsBucket(item, granularity = 'TOTAL', index = 0) {
  if (!item || typeof item !== 'object') return null

  const callsPlaced = pickNumber(
    item.calls_placed,
    item.callsPlaced,
    item.placed,
    item.total_calls,
    item.totalCalls,
  ) || 0
  const callsConnected = pickNumber(
    item.calls_connected,
    item.callsConnected,
    item.connected,
  ) || 0
  const callsNoAnswer = pickNumber(
    item.calls_no_answer,
    item.callsNoAnswer,
    item.no_answer,
    item.noAnswer,
  ) || 0
  const callsFailed = pickNumber(
    item.calls_failed,
    item.callsFailed,
    item.failed,
  ) || 0
  const avgLeadScore = pickNumber(
    item.avg_lead_score,
    item.avgLeadScore,
    item.average_lead_score,
  )
  // AGENT buckets carry connect rate as call_connect_rate (their own calls,
  // scoped on read) -- connect_rate is the lead-history-derived TOTAL field.
  const connectRate = pickNumber(
    item.connect_rate,
    item.connectRate,
    item.call_connect_rate,
    item.callConnectRate,
  )
  const conversionRate = pickNumber(
    item.conversion_rate,
    item.conversionRate,
  )
  const uniqueLeadsCalled = pickNumber(
    item.unique_leads_called,
    item.uniqueLeadsCalled,
  )
  const totalTalkTimeSeconds = pickNumber(
    item.total_talk_time_seconds,
    item.totalTalkTimeSeconds,
  )
  // metric_key IS the real source for all four of these -- see
  // parseMetricKey's own comment. The direct item.* checks are kept first
  // in case some other response shape ever supplies them directly.
  const gran = String(granularity || '').toUpperCase()
  const { value: keyValue } = parseMetricKey(item)
  const hour = pickNumber(item.hour, item.hour_of_day, item.hourOfDay, gran === 'HOUR_OF_DAY' ? keyValue : null)
  const dayOfWeek = pickString(
    item.day_of_week,
    item.dayOfWeek,
    item.weekday,
    gran === 'DAY_OF_WEEK' ? keyValue : '',
  )
  const date = pickString(item.date, item.day, gran === 'DAY' ? keyValue : '')
  const agentId = pickString(item.agent_id, item.agentId, item.user_id, gran === 'AGENT' ? keyValue : '')
  // Leaderboard "vs last period" arrows/streak -- only present when the
  // caller passed a date range (see backend); null on the lifetime view.
  const rankMovement = pickNumber(item.rank_movement, item.rankMovement)
  const streakPeriods = pickNumber(item.streak_periods, item.streakPeriods)

  return {
    id: pickString(item.id, item.key, agentId, date, String(hour), dayOfWeek) || `bucket-${index}`,
    label: bucketLabel(item, granularity, index),
    granularity: String(granularity || 'TOTAL').toUpperCase(),
    hour,
    dayOfWeek,
    date,
    agentId,
    callOutcomes: extractCallOutcomes(item),
    callDurations: extractCallDurations(item),
    hourly: extractHourly(item),
    uniqueLeadsCalled,
    totalTalkTimeSeconds,
    totalTalkTimeLabel: formatTalkTime(totalTalkTimeSeconds),
    rankMovement,
    streakPeriods,
    callsPlaced,
    callsConnected,
    callsNoAnswer,
    callsFailed,
    avgLeadScore,
    avgLeadScoreLabel: formatScore(avgLeadScore),
    connectRate,
    connectRateLabel: formatRate(connectRate),
    conversionRate,
    conversionRateLabel: formatRate(conversionRate),
    statusCounts: extractStatusCounts(item),
    raw: item,
  }
}

export function normalizeCampaignMetricsResponse(response, granularity = 'TOTAL') {
  const root = response?.data && typeof response.data === 'object'
    ? response.data
    : response
  const gran = String(
    root?.granularity ?? granularity ?? 'TOTAL',
  ).toUpperCase()
  const buckets = extractBuckets(response)
    .map((item, index) => normalizeCampaignMetricsBucket(item, gran, index))
    .filter(Boolean)

  const bestRaw = root?.best ?? response?.best ?? null
  const best = bestRaw
    ? normalizeCampaignMetricsBucket(bestRaw, gran, 0)
    : null

  return {
    granularity: gran,
    buckets,
    best,
    total: buckets[0] || null,
    // TOTAL-only: distinct leads called, computed on read (see backend) --
    // sits beside buckets rather than inside one, so it's picked up here.
    uniqueLeadsCalled: pickNumber(root?.unique_leads_called, root?.uniqueLeadsCalled),
    message: String(response?.message ?? root?.message ?? '').trim(),
  }
}

/** By Status / dispo breakdown tab -- getTaskStatusMetrics response. */
export function normalizeCampaignStatusMetricsResponse(response) {
  const root = response?.data && typeof response.data === 'object'
    ? response.data
    : response
  const statuses = (Array.isArray(root?.statuses) ? root.statuses : [])
    .map((row, index) => {
      const status = pickString(row?.status) || `Status ${index + 1}`
      const count = pickNumber(row?.count) || 0
      const pct = pickNumber(row?.pct) ?? 0
      const avgLeadScore = pickNumber(row?.avg_lead_score, row?.avgLeadScore)
      const totalCalls = pickNumber(row?.total_calls, row?.totalCalls)
      const uniqueCalls = pickNumber(row?.unique_calls, row?.uniqueCalls)
      const conversionRate = pickNumber(row?.conversion_rate, row?.conversionRate)
      return {
        id: `status-${index}`,
        status,
        // status stays the RAW backend value (used for matching against a
        // lead's own request_status when filtering) -- label is the
        // cosmetic display form. They differ for system defaults like the
        // literal "PENDING" a lead starts in (never org-typed, so it never
        // got the title-casing an org's own custom statuses have).
        label: titleCaseStatus(status),
        count,
        pct,
        pctLabel: formatRate(pct),
        avgLeadScore,
        avgLeadScoreLabel: formatScore(avgLeadScore),
        totalCalls,
        uniqueCalls,
        conversionRate,
        conversionRateLabel: formatRate(conversionRate),
      }
    })
    .sort((a, b) => b.count - a.count)

  return {
    statuses,
    total: pickNumber(root?.total) || 0,
    snapshotMissing: !!(root?.snapshot_missing ?? root?.snapshotMissing),
    agentId: pickString(root?.agent_id, root?.agentId),
    message: String(response?.message ?? root?.message ?? '').trim(),
  }
}

export function normalizeCampaignMetricsDrilldownItem(item, index = 0) {
  if (!item || typeof item !== 'object') return null
  const requestId = pickString(
    item.request_id,
    item.requestId,
    item.lead_id,
    item.leadId,
  )
  const name = pickHumanLabel(
    item.caller_name,
    item.callerName,
    item.contact_name,
    item.contactName,
    item.name,
    item.lead_name,
    item.leadName,
  )
  const status = pickString(item.request_status, item.requestStatus, item.status)
  const callStatus = pickString(item.call_status, item.callStatus)
  const calledOn =
    item.called_on ??
    item.calledOn ??
    item.start_time ??
    item.startTime ??
    item.created_on ??
    item.createdOn
  const duration = pickNumber(item.duration_seconds, item.durationSeconds, item.duration)
  const score = pickNumber(item.lead_score, item.leadScore)
  const agentName = pickHumanLabel(
    item.agent_name,
    item.agentName,
    item.performed_by,
    item.performedBy,
  )
  const callUuid = pickString(
    item.call_uuid,
    item.callUuid,
    item.caller_id,
    item.callerId,
  )
  const recordingUrl = pickString(
    item.recording_s3_url,
    item.recordingS3Url,
    item.recording_url,
    item.recordingUrl,
  )
  const comments = pickString(item.comments, item.comment, item.lead_overview, item.leadOverview)
  const phone = pickString(item.from_number, item.fromNumber, item.phone, item.contact_number)

  return {
    id: callUuid || requestId || `row-${index}`,
    requestId,
    callUuid,
    name: name || phone || 'Lead',
    phone,
    status,
    statusLabel: status ? formatLabel(status) : '',
    callStatus,
    callStatusLabel: callStatus ? formatLabel(callStatus) : '',
    calledOn,
    calledOnLabel: calledOn ? formatDateTime(calledOn) : '—',
    durationSeconds: duration || 0,
    durationLabel: duration ? formatCallDuration(duration) : '',
    leadScore: score,
    leadScoreLabel: score != null ? formatScore(score) : '—',
    agentName: agentName || '',
    comments,
    overview: comments,
    recordingUrl,
    kind: pickString(item._kind) || (callStatus || recordingUrl || duration ? 'call' : 'status'),
    raw: item,
  }
}

function titleCaseStatus(label) {
  const text = String(label || '').trim()
  if (!text || text === '—') return text
  return text
    .toLowerCase()
    .replace(/\b\w/g, (ch) => ch.toUpperCase())
}

function toEpochMs(value) {
  if (value == null || value === '') return 0
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value
  }
  const raw = String(value).trim()
  if (/^\d+(\.\d+)?$/.test(raw)) {
    const num = Number(raw)
    return num < 1e12 ? num * 1000 : num
  }
  const parsed = Date.parse(raw)
  return Number.isFinite(parsed) ? parsed : 0
}

/** Normalize history rows for the under-card timeline (oldest → newest). */
export function normalizeDrilldownTimeline(entries = []) {
  return (Array.isArray(entries) ? entries : [])
    .map((entry, index) => {
      if (!entry || typeof entry !== 'object') return null
      const status = pickString(entry.request_status, entry.requestStatus)
      const createdOn = entry.created_on ?? entry.createdOn ?? null
      const score = pickNumber(entry.lead_score, entry.leadScore)
      return {
        id: pickString(entry.history_id, entry.historyId) || `hist-${index}`,
        status,
        statusLabel: titleCaseStatus(status ? formatLabel(status) : ''),
        comments: pickString(entry.comments, entry.comment),
        performedBy: pickHumanLabel(entry.performed_by, entry.performedBy),
        createdOn,
        createdOnMs: toEpochMs(createdOn),
        createdOnLabel: createdOn ? formatDateTime(createdOn) : '',
        leadScore: score,
        leadScoreLabel: score != null ? formatScore(score) : '',
      }
    })
    .filter(Boolean)
    .sort((a, b) => (a.createdOnMs || 0) - (b.createdOnMs || 0))
}

/**
 * Drilldown returns { calls, history }. Merge on call_uuid / caller_id so
 * lead status + comments + score attach to the call recording row.
 */
export function normalizeCampaignMetricsDrilldownResponse(response) {
  const root = response?.data && typeof response.data === 'object'
    ? response.data
    : response

  const calls = Array.isArray(root?.calls) ? root.calls : []
  const history = Array.isArray(root?.history) ? root.history : []

  // Legacy flat lists
  if (!calls.length && !history.length) {
    const items = Array.isArray(root?.items)
      ? root.items
      : Array.isArray(root?.leads)
        ? root.leads
        : Array.isArray(root)
          ? root
          : Array.isArray(response?.items)
            ? response.items
            : []
    return {
      items: items
        .map((item, index) => {
          const row = normalizeCampaignMetricsDrilldownItem(item, index)
          if (!row) return null
          return {
            ...row,
            statusLabel: titleCaseStatus(row.statusLabel),
            callStatusLabel: titleCaseStatus(row.callStatusLabel),
            timeline: [],
          }
        })
        .filter(Boolean),
      totalCalls: pickNumber(root?.total_calls, root?.totalCalls) ?? items.length,
      totalHistory: pickNumber(root?.total_history, root?.totalHistory) ?? 0,
      message: String(response?.message ?? root?.message ?? '').trim(),
    }
  }

  const historyByCall = new Map()
  const unlinkedHistory = []

  for (const entry of history) {
    const key = pickString(entry?.call_uuid, entry?.callUuid)
    if (key) {
      const bucket = historyByCall.get(key) || []
      bucket.push(entry)
      historyByCall.set(key, bucket)
    } else if (entry) {
      unlinkedHistory.push(entry)
    }
  }

  const pickPrimaryHistory = (entries = []) => {
    if (!entries.length) return null
    const scored = entries.filter((e) => e.lead_score != null || e.leadScore != null)
    const pool = scored.length ? scored : entries
    return [...pool].sort((a, b) => {
      const aOn = Number(a.created_on ?? a.createdOn ?? 0)
      const bOn = Number(b.created_on ?? b.createdOn ?? 0)
      return bOn - aOn
    })[0]
  }

  const items = []

  calls.forEach((call, index) => {
    const callUuid = pickString(call?.call_uuid, call?.callUuid, call?.caller_id, call?.callerId)
    const linked = callUuid ? historyByCall.get(callUuid) || [] : []
    if (callUuid) historyByCall.delete(callUuid)
    const primary = pickPrimaryHistory(linked)
    const comments =
      pickString(primary?.comments, primary?.comment) ||
      linked
        .map((entry) => pickString(entry.comments, entry.comment))
        .filter(Boolean)
        .join('\n\n')

    const merged = {
      ...call,
      ...(primary || {}),
      // Call fields win for telephony; history wins for disposition.
      caller_name: pickHumanLabel(
        call.caller_name,
        call.callerName,
        primary?.contact_name,
        primary?.contactName,
        primary?.caller_name,
        primary?.callerName,
      ),
      call_status: pickString(call.call_status, call.callStatus),
      duration_seconds: call.duration_seconds ?? call.durationSeconds ?? call.duration,
      recording_s3_url: pickString(call.recording_s3_url, call.recordingS3Url),
      start_time: call.start_time ?? call.startTime ?? call.created_on,
      request_status: pickString(primary?.request_status, primary?.requestStatus),
      lead_score: primary?.lead_score ?? primary?.leadScore ?? null,
      performed_by: pickHumanLabel(primary?.performed_by, primary?.performedBy),
      agent_name: pickHumanLabel(
        call.agent_name,
        call.agentName,
        primary?.agent_name,
        primary?.agentName,
      ),
      comments,
      call_uuid: callUuid,
      _kind: 'call',
    }

    const row = normalizeCampaignMetricsDrilldownItem(merged, index)
    if (!row) return
    items.push({
      ...row,
      statusLabel: titleCaseStatus(row.statusLabel),
      callStatusLabel: titleCaseStatus(row.callStatusLabel),
      statusUpdates: linked.length,
      timeline: normalizeDrilldownTimeline(linked),
    })
  })

  // Remaining history keyed by call_uuid with no matching call
  for (const leftover of historyByCall.values()) {
    unlinkedHistory.push(...leftover)
  }

  unlinkedHistory.forEach((entry, index) => {
    const row = normalizeCampaignMetricsDrilldownItem(
      { ...entry, _kind: 'status' },
      calls.length + index,
    )
    if (!row) return
    items.push({
      ...row,
      statusLabel: titleCaseStatus(row.statusLabel),
      callStatusLabel: titleCaseStatus(row.callStatusLabel),
      timeline: normalizeDrilldownTimeline([entry]),
    })
  })

  items.sort((a, b) => {
    const aMs = Number(a.calledOn) || 0
    const bMs = Number(b.calledOn) || 0
    return bMs - aMs
  })

  return {
    items,
    totalCalls: pickNumber(root?.total_calls, root?.totalCalls) ?? calls.length,
    totalHistory: pickNumber(root?.total_history, root?.totalHistory) ?? history.length,
    message: String(response?.message ?? root?.message ?? '').trim(),
  }
}

/** Build drilldown filters from a metrics bucket click. */
export function drilldownInputFromBucket(bucket, granularity) {
  if (!bucket) return {}
  const gran = String(granularity || bucket.granularity || '').toUpperCase()
  const input = {}
  if (gran === 'HOUR_OF_DAY' && bucket.hour != null) {
    input.hour = Math.floor(bucket.hour)
  }
  if (gran === 'DAY_OF_WEEK' && bucket.dayOfWeek) {
    input.day_of_week = bucket.dayOfWeek
  }
  if (gran === 'DAY' && bucket.date) {
    input.date_from = bucket.date
    input.date_to = bucket.date
  }
  if (gran === 'AGENT' && bucket.agentId) {
    input.agent_id = bucket.agentId
  }
  // By Status tab: click a status row -> see just those leads/history.
  if (gran === 'STATUS' && bucket.status) {
    input.request_status = bucket.status
  }
  // Call outcomes tile: click Connected/No answer/Failed -> only those calls.
  if (gran === 'OUTCOME' && bucket.outcome) {
    input.call_outcome = CALL_OUTCOME_KEY[bucket.outcome] || bucket.outcome.toLowerCase().replace(/\s+/g, '_')
  }
  // Call duration tile: click Short/Medium/Long -> only those calls.
  if (gran === 'DURATION' && bucket.bucket) {
    input.call_duration = CALL_DURATION_KEY[bucket.bucket] || ''
  }
  return input
}

const CALL_OUTCOME_KEY = { Connected: 'connected', 'No answer': 'no_answer', Failed: 'failed' }
const CALL_DURATION_KEY = { 'Short (<10s)': 'short', 'Medium (10-30s)': 'medium', 'Long (>30s)': 'long' }
